// This file is shared for SSR and CSR.  The functions define the async calls for getting data
import axios from 'axios';
import axiosRetry from 'axios-retry';
import he from 'he';
import moment from 'moment-timezone';
import {
  RETRY_COUNT,
  CONTENT_TYPES,
} from '../common/constants';
import { isNotEmpty } from '../common/utilities';
import { getUserTimezone } from '../common/utilitiesTime';
import styleConstants from '../common/styleConstants';
// import { updateFields } from './coachingRequestManagementSlice';

axiosRetry(axios, { retries: RETRY_COUNT });

export const getLanguageDisplayName = (languageCode) => {
  if (!languageCode) return '';

  try {
    // Use Intl.DisplayNames to get the language name from the language code
    const displayNames = new Intl.DisplayNames(['en'], { type: 'language' });
    return displayNames.of(languageCode);
  } catch (error) {
    // Fallback: return the language code if Intl.DisplayNames fails
    return languageCode;
  }
};

// NOTE: Change service calls to use this function
export const getStatusMap = () => ({
  1: { label: 'New', color: styleConstants.COLORS.accent.gold, fontColor: styleConstants.COLORS.primary.black },
  2: { label: 'Queued', color: styleConstants.COLORS.accent.iris, fontColor: styleConstants.COLORS.primary.white },
  3: { label: 'Scheduled', color: styleConstants.COLORS.accent.iris, fontColor: styleConstants.COLORS.primary.white },
  4: { label: 'Complete', color: styleConstants.COLORS.detail['slate-light'], fontColor: styleConstants.COLORS.primary.black },
  5: { label: 'Cancelled', color: styleConstants.COLORS.detail['slate-light'], fontColor: styleConstants.COLORS.primary.black },
});

export const getStatusColorByLabel = (status) => {
  switch (status?.toLowerCase()) {
  case 'requested':
    return styleConstants.COLORS.accent.gold;
  case 'in progress':
    return styleConstants.COLORS.accent.iris;
  case 'complete':
    return styleConstants.COLORS.accent.plant;
  case 'closed':
    return styleConstants.COLORS.detail['slate-light'];
  default:
    return styleConstants.COLORS.detail['slate-light'];
  }
};

export const makeUpdateObject = (fieldsToUpdate) => {
  const coachingRequestIds = [];
  const data = [];
  fieldsToUpdate.forEach((item) => {
    const { updateColumnName, updateValueKey } = item.fieldData;
    const { value } = item.newValue;
    if (!coachingRequestIds.includes(item.fieldData.coachingRequestId)) {
      coachingRequestIds.push(item.fieldData.coachingRequestId);
    }
    const updateObject = {
      columnName: updateColumnName,
      value: updateValueKey ? value[updateValueKey] : value,
    };

    if (Array.isArray(value) && value.length > 0) {
      updateObject.value = updateValueKey ? value[0][updateValueKey] : value[0];
    }
    if (updateObject.columnName) data.push(updateObject);
  });
  return ({ CoachingRequestIds: [...new Set(coachingRequestIds)], Data: data });
};

export const makeFieldObject = ({
  updateColumnName,
  updateField,
  value,
  label,
  displayLabel = undefined,
  editable,
  type,
  id,
  disabled = false,
  canBeBlank = false,
  placeHolder,
  isRequired,
  selectOption = [],
  updateObject = undefined,
  coachingRequestId = undefined,
  multiple = false,
  updateValueKey = undefined,
  editableOverride = undefined,
  showTooltip = false,
  additionalInfo = undefined,
}) => {
  const returnValue = {
    coachingRequestId,
    value: value === undefined ? '' : value,
    label,
    displayLabel,
    isEditable: editable,
    showTooltip,
    type: type === undefined ? 'default' : type,
    disabled,
    canBeBlank,
    selectOption,
    updateValueKey,
    editableOverride,
  };
  if (placeHolder !== undefined) {
    returnValue.placeHolder = placeHolder;
  }
  if (isRequired !== undefined) {
    returnValue.isRequired = isRequired;
  }

  returnValue.updateObject = (({ fieldType }) => {
    if (updateObject === undefined && fieldType?.indexOf('elect') > -1) {
      return (val) => ({
        id: val?.id || -1,
        label: val?.label || 'Unassigned',
      });
    }
    return updateObject;
  })({ fieldType: type });

  if (updateColumnName !== undefined) {
    returnValue.updateColumnName = updateColumnName;
  }
  if (updateField !== undefined) {
    returnValue.updateField = updateField;
  }
  if (id !== undefined) {
    returnValue.id = id;
  }
  if (multiple) {
    returnValue.value = value.map((e) => ({ ...e, value: e.label }));
    returnValue.multiple = true;
  }
  if (additionalInfo !== undefined) {
    returnValue.additionalInfo = additionalInfo;
  }
  return returnValue;
};

const getPicklistItemById = (id, picklists) => {
  let returnValue = { value: '', id: '', label: '' };
  if (isNotEmpty(picklists) && isNotEmpty(id)) {
    const found = Object.keys(picklists).length && picklists.find((item) => item.id === id);
    if (found) returnValue = { value: found.label, id: found.id, label: found.label };
  }
  return returnValue;
};

export const adaptModelRequestListToTableData = ({ requestList, picklists, coachingTypeObj }) => {
  const returnValue = {
    headerData: [],
    data: [],
    picklists: {},
  };
  if (typeof requestList === 'undefined') {
    return returnValue;
  }
  returnValue.picklists = picklists;

  const coachingTypeId = coachingTypeObj?.id || undefined;
  let coachingLabel = 'Topic';
  if (coachingTypeId && coachingTypeId === 1) coachingLabel = 'Product(s)';
  else if (!coachingTypeId) coachingLabel = 'Product(s)/Topic';

  // Handle Fast Track specific headers
  const isFastTrack = coachingTypeId === 5;

  const possibleHeadersMap = {
    customerAccount: {
      id: '0', label: 'Customer Account', type: isFastTrack ? 'FastTrackCustomerAccount' : 'Link', sortKey: 'CustomerCompany',
    },
    status: {
      id: '1', label: 'Status', type: isFastTrack ? 'StatusSelector' : 'ChipSelector', sortKey: 'StatusName',
    },
    coachingType: {
      id: '2', label: coachingLabel, type: 'Default', sortKey: 'CoachingSubType',
    },
    topic: {
      id: '2', label: 'Topic', type: 'Default', sortKey: 'CoachingSubType',
    },
    coach: {
      id: '3',
      label: isFastTrack ? 'Assigned Lead' : 'Coach',
      type: 'Select',
      sortKey: 'CoachName',
    },
    daysInQueue: { id: '4', label: 'Days in Queue', sortKey: 'DaysInQueue' },
    endDateTime: {
      id: '5', label: 'Course Completion Date', type: 'datetime', sortKey: 'EndDateTime',
    },
  };

  returnValue.headerData = (() => {
    // Determine the header details
    const retVal = [];
    if (isNotEmpty(requestList)) {
      if (isFastTrack) {
        // Fast Track specific headers in exact order
        const fastTrackHeaders = [
          'customerAccount',
          'status',
          'topic',
          'coach',
          'daysInQueue',
        ];
        fastTrackHeaders.forEach((key) => {
          if (possibleHeadersMap[key]) {
            retVal.push({
              id: possibleHeadersMap[key].id,
              label: possibleHeadersMap[key].label,
              type: possibleHeadersMap[key].type || 'Default',
              key,
              sortKey: possibleHeadersMap[key].sortKey,
            });
          }
        });
      } else {
        // Regular coaching request headers (dynamic based on data)
        // If the API returns a field, it will be shown as a column
        const item = { ...requestList[0] };
        Object.keys(item).forEach((key) => {
          if (possibleHeadersMap[key]) {
            retVal.push({
              id: possibleHeadersMap[key].id,
              label: possibleHeadersMap[key].label,
              type: possibleHeadersMap[key].type || 'Default',
              key,
              sortKey: possibleHeadersMap[key].sortKey,
            });
          }
        });
      }
    }
    return retVal;
  })();

  returnValue.data = requestList?.map((item) => {
    const row = {};
    row.coachingRequestId = item.coachingRequestId;
    row.customerAccount = {
      company: item.customerAccount.company ? item.customerAccount.company : '',
      email: item?.customerAccount?.email ? item?.customerAccount?.email : '',
      firstName: item?.customerAccount?.firstName ? item?.customerAccount?.firstName : '',
      lastName: item?.customerAccount?.lastName ? item?.customerAccount?.lastName : '',
    };

    // Handle Fast Track data differently
    if (isFastTrack) {
      // For Fast Track, handle both mock data format and API format
      if (typeof item.status === 'string') {
        // Mock data format - status is a string
        row.status = item.status;
      } else {
        // API format - status is an object
        row.status = item.status?.label || item.status;
      }

      // For Fast Track, populate topic from coachingSubTypes[0].label
      row.topic = item.coachingSubTypes?.length > 0 ? item.coachingSubTypes[0].label : item.topic;

      // Create field object for coach (displayed as "Assigned Lead" for Fast Track)
      row.coach = makeFieldObject({
        id: item?.coach?.userId || '',
        value: (item?.coach?.firstName && item?.coach?.lastName)
          ? `${item?.coach?.firstName} ${item?.coach?.lastName}` : 'Unassigned',
        type: 'coachSelect',
        editable: true,
        coachingRequestId: item.coachingRequestId,
        placeHolder: 'Select Lead',
        updateColumnName: 'CoachUserId',
        updateField: 'coach',
        updateValueKey: 'userId',
        updateObject: (value) => ({
          firstName: value.coachFirstName,
          lastName: value.coachLastName,
          userId: value.userId,
          email: value.coachEmail,
          coachingTypeId: value.coachingTypeId,
        }),
      });

      row.daysInQueue = item.daysInQueue;
      // Map topic to coachingType field for backward compatibility
      row.coachingType = item.topic;
    } else {
      // For regular coaching requests, use field objects
      row.status = makeFieldObject({
        id: item.status.id,
        value: item.status.label,
        type: 'chipSelect',
        placeHolder: 'Select',
        editable: true,
        coachingRequestId: item.coachingRequestId,
        updateColumnName: 'CoachingRequestStatusId',
        updateField: 'status',
        updateValueKey: 'id',
      });
      row.product = item.coachingSubTypes.map((i) => i.label).join(', ');
      // row.coach = (item.coach.userId && picklists.coach) ? getCoachName({ userId: item.coach.userId, coachList: picklists.coach }) : '';
      row.coach = makeFieldObject({
        id: item?.coach?.userId || '',
        value: (item?.coach?.firstName && item?.coach?.lastName) ? `${item?.coach?.firstName} ${item?.coach?.lastName}` : 'Unassigned',
        type: 'coachSelect',
        editable: true,
        coachingRequestId: item.coachingRequestId,
        placeHolder: 'Select',
        updateColumnName: 'CoachUserId',
        updateField: 'coach',
        updateValueKey: 'userId',
        updateObject: (value) => ({
          firstName: value.coachFirstName,
          lastName: value.coachLastName,
          userId: value.userId,
          email: value.coachEmail,
          coachingTypeId: value.coachingTypeId,
        }),
      });
      row.daysInQueue = item.daysInQueue;

      // Add endDateTime if present in data, formatted in user's timezone
      if (item.endDateTime !== undefined) {
        const userTimezone = getUserTimezone();
        const endDateMoment = item.endDateTime ? moment.utc(item.endDateTime) : null;
        row.endDateTime = endDateMoment?.isValid()
          ? endDateMoment.tz(userTimezone).format('MMM DD, YYYY h:mm A')
          : '';
      }
    }
    return row;
  });
  return returnValue;
};

export const adaptModelRequestDetailsToComponentData = ({
  requestDetails, picklists, isUserCoachingManager, userId, requestViewOnly,
}) => {
  const returnValue = {};
  if (typeof requestDetails === 'undefined') {
    return returnValue;
  }

  const editableOverride = userId === requestDetails?.coach?.userId || isUserCoachingManager;

  returnValue.picklists = picklists;

  returnValue.status = makeFieldObject({
    id: requestDetails.status.id,
    value: requestDetails.status.label,
    type: 'chipSelect',
    placeHolder: 'Select',
    editable: true,
    coachingRequestId: requestDetails.coachingRequestId,
    updateColumnName: 'CoachingRequestStatusId',
    updateField: 'status',
    updateValueKey: 'id',
  });

  // Determine the header details
  returnValue.identity = {
    companyName: {
      ...makeFieldObject({
        coachingRequestId: requestDetails?.coachingRequestId,
        updateColumnName: 'CustomerCompany',
        updateField: 'customerAccount.company',
        value: requestDetails?.customerAccount !== null ? requestDetails?.customerAccount.company : '',
        label: 'Company name',
        displayLabel: '',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(isUserCoachingManager && !requestViewOnly),
      }),
    },
    accountCsn: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Account CSN',
        updateField: 'accountCSN',
        value: requestDetails?.accountCSN !== null ? requestDetails?.accountCSN : '',
        displayLabel: '',
        editable: !requestViewOnly,
        showTooltip: requestViewOnly,
      }),
    },
  };
  returnValue.requestor = {
    timeReceived: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: requestDetails?.receivedDate !== null ? requestDetails?.receivedDate : '',
        label: 'Received',
        editable: false,
        type: 'datetime',
      }),
    },
    contactPersonName: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: `${requestDetails?.customerAccount?.firstName ? `${requestDetails?.customerAccount?.firstName} ` : ''}${requestDetails?.customerAccount?.lastName || ''}`,
        editable: false,
        label: 'Requestor',
      }),
    },
    contactPersonEmail: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: requestDetails?.customerAccount !== null ? requestDetails?.customerAccount?.email : '',
        editable: false,
      }),
    },
    referredByName: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: `${requestDetails?.submitter?.firstName ? `${requestDetails?.submitter?.firstName} ` : ''}${requestDetails?.submitter?.lastName || ''}`,
        editable: false,
        type: 'default',
        id: requestDetails.submitter !== null ? requestDetails.submitter.userId : '',
        label: 'Referred by',
      }),
    },
    referredByEmail: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: requestDetails?.submitter !== null ? requestDetails?.submitter?.email : '',
        editable: false,
        type: 'default',
        id: requestDetails.submitter !== null ? requestDetails.submitter.userId : '',
      }),
    },
  };
  returnValue.contacts = {
    coach: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'CoachUserId',
        updateField: 'coach',
        value: `${requestDetails?.coach !== null ? `${requestDetails?.coach?.firstName} ${requestDetails?.coach?.lastName}` : ''}`,
        label: 'Coach',
        editable: isUserCoachingManager && !requestViewOnly,
        showTooltip: !(isUserCoachingManager && !requestViewOnly),
        type: 'select',
        id: requestDetails.coach !== null ? requestDetails.coach.userId : '',
        selectOption: picklists.coaches,
        updateObject: (value) => ({
          firstName: value?.coachFirstName || '',
          lastName: value?.coachLastName || '',
          userId: value.userId || '',
          email: value.coachEmail || '',
          coachingTypeId: value.coachingTypeId || '',
        }),
        updateValueKey: 'userId',
      }),
    },
    partnerContactName: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Partner Name',
        updateField: 'partnerName',
        value: requestDetails?.partnerName !== null ? requestDetails?.partnerName : '',
        label: 'Partner contact',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
      }),
    },
    partnerContactEmail: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Partner Contact',
        updateField: 'partnerContact',
        value: requestDetails.partnerContact !== null ? requestDetails.partnerContact : '',
        label: 'Partner contact email',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'email',
      }),
    },
  };
  returnValue.schedule = {
    daysSinceLastSession: (requestDetails?.daysSinceLastSession !== null && requestDetails?.daysSinceLastSession >= 0) ? requestDetails?.daysSinceLastSession : -1,
    discoveryDate: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Discovery Date',
        updateField: 'discoveryDate',
        value: requestDetails.discoveryDate !== null ? requestDetails.discoveryDate : '',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'datetime',
      }),
    },
    classId: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Class Id',
        updateField: 'classId',
        value: requestDetails?.classId !== null ? requestDetails?.classId : '',
        label: 'Class Id',
        editable: true,
      }),
    },
    deliveryDate: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Delivery Date',
        updateField: 'deliveryDate',
        value: requestDetails.deliveryDate !== null ? requestDetails.deliveryDate : '',
        editable: !requestViewOnly,
        showTooltip: requestViewOnly,
        type: 'datetime',
      }),
    },
    scheduleDate: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Scheduled Date',
        updateField: 'scheduleDate',
        value: requestDetails?.scheduleDate !== null ? requestDetails?.scheduleDate : '',
        editable: !requestViewOnly,
        showTooltip: requestViewOnly,
        type: 'datetime',
      }),
    },
    customerAvailability: requestDetails.customerAvailability.length > 0 ? requestDetails.customerAvailability : [],
  };
  returnValue.notes = {
    notes: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Notes',
        updateField: 'notes',
        value: requestDetails.notes.length > 0 ? he.decode(requestDetails.notes[0].value) : '',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'multiline',
        updateObject: (value) => ([{
          userId: value.userId,
          value,
          dateCreated: Date.now(),
        }]),
        updateValueKey: 'value',
      }),
    },
    lastModifiedDate: requestDetails.lastModifiedDate !== null ? requestDetails.lastModifiedDate : '',
  };
  returnValue.objectives = {
    objectives: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'customerObjectives',
        updateField: 'customerObjectives',
        value: requestDetails.customerObjectives !== null ? requestDetails.customerObjectives : '',
        editable: false,
        type: 'multiline',
      }),
    },
  };
  returnValue.location = {
    geo: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: undefined,
        updateField: 'geo',
        value: requestDetails?.geo !== null ? requestDetails?.geo?.label : '',
        label: 'Geo',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        isRequired: true,
        canBeBlank: true,
        placeHolder: 'Select Geo',
        selectOption: picklists.geo,
        updateObject: (value) => ({
          id: value.value,
          label: value.label,
          value: value.label,
        }),
      }),
    },
    country: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'GeoCountryRelationId',
        updateField: 'country',
        value: requestDetails.country !== null ? requestDetails?.country.label : '',
        label: 'Country',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        disabled: true,
        selectOption: picklists.country,
        placeHolder: 'Select Country',
        updateValueKey: 'id',
        updateObject: (value) => ({
          id: value.value,
          label: value.label,
          value: value.label,
        }),
      }),
    },
    language: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'LanguageCode',
        updateField: 'language',
        value: requestDetails?.language ? getLanguageDisplayName(requestDetails.language) : '',
        label: 'Language',
        editable: false,
        showTooltip: false,
        type: 'text',
      }),
    },
  };

  returnValue.details = {
    industry: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Industry',
        updateField: 'industry',
        value: getPicklistItemById(requestDetails.industry, picklists?.industrySubSegments).label,
        id: requestDetails?.industry !== null ? requestDetails?.industry.id : '',
        label: 'Industry',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        selectOption: picklists.industry,
        updateObject: (value) => (value.id),
      }),
    },
    capabilities: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Capabilities',
        updateField: 'capabilities',
        value: getPicklistItemById(requestDetails.capabilities, picklists?.capabilities).label,
        id: requestDetails.capabilities !== null ? requestDetails.capabilities.id : '',
        label: 'Capabilities',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        selectOption: picklists.capabilities,
        updateObject: (value) => (value.id),
      }),
    },
    outcomes: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Outcomes',
        updateField: 'outcomes',
        value: getPicklistItemById(requestDetails.outcomes, picklists?.outcomes).label,
        id: requestDetails.outcomes !== null ? requestDetails.outcomes.id : '',
        label: 'Outcomes',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        selectOption: picklists.outcomes,
        updateObject: (value) => (value.id),
      }),
    },
    coachingSubTypes: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'coachingSubTypes',
        updateField: 'coachingSubTypes',
        value: requestDetails?.coachingSubTypes.length > 0 ? requestDetails?.coachingSubTypes.map((req) => ({ ...req, children: req.label })) : [],
        label: requestDetails.coachingType.id === 1 ? `Product${requestDetails?.coachingSubTypes.length > 1 ? 's' : ''}` : 'Topic',
        editable: false,
        type: requestDetails.coachingType.id === 1 ? 'multiSelect' : 'select',
        selectOption: picklists?.coachingSubTypes,
        multiple: requestDetails.coachingType.id === 1,
        updateObject: (value) => value,
      }),
    },
  };
  return returnValue;
};

export const formatDate = (date) => {
  if (date) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
    return formatter.format(new Date(date));
  }
  return '';
};

const findContentType = (content) => {
  if (content.type === CONTENT_TYPES.LearnLiveClass.type) return CONTENT_TYPES.LearnLiveClass;
  if (content.type === CONTENT_TYPES.LearnLiveCourse.type) return CONTENT_TYPES.LearnLiveCourse;
  if (content && content.uri) {
    const keyword = content.uri.split('/')[2];
    return CONTENT_TYPES[keyword] || null;
  }
  return null;
};

const calculateEffort = (effort) => {
  if (effort === undefined || effort === null) return '';
  const parsedEffort = parseInt(effort, 10);
  if (parsedEffort < 60) return `${parsedEffort} min.`;
  if (parsedEffort % 60 === 0) return `${(parsedEffort / 60)} hr.`;
  return `${Math.floor(parsedEffort / 60)} hr. ${(parsedEffort % 60)} min.`;
};

const createContentItem = (item) => {
  const response = {
    id: item.id,
    title: item.title,
    effort: calculateEffort(item.effort),
    type: findContentType(item),
    uri: item.uri ? item.uri : null,
    isRemoved: typeof (item.isRemoved) === 'undefined' ? true : item.isRemoved,
  };
  if (response.type === CONTENT_TYPES.LearnLiveClass) {
    response.coach = `${item.instructor?.firstName} ${item.instructor?.lastName}`;
    response.schedule = { start: item?.startDatetime, end: item?.endDatetime };
  }
  return response;
};

const calculateSelectedItems = (section) => {
  let learningItemsTotal = 0;
  let coachingSessionsTotal = 0;
  let coachingSessionsSelected = 0;
  let learningItemsSelected = 0;
  let notScheduled = 0;
  if (section.contentItems === undefined || section.contentItems.length === 0) {
    return {
      coachingSessionsTotal: 0,
      coachingSessionsSelected: 0,
      learningItemsTotal: 0,
      learningItemsSelected: 0,
      notScheduled: 0,
    };
  }
  section.contentItems.forEach((item) => {
    if (item.type?.type === CONTENT_TYPES.LearnLiveClass.type || item.type?.type === CONTENT_TYPES.LearnLiveCourse.type) {
      coachingSessionsTotal += 1;
      if (!item.isRemoved) coachingSessionsSelected += 1;
      if (!item.isRemoved && item.type?.type === CONTENT_TYPES.LearnLiveCourse.type) notScheduled += 1;
    } else {
      learningItemsTotal += 1;
      if (!item.isRemoved) learningItemsSelected += 1;
    }
  });
  return {
    coachingSessionsTotal,
    coachingSessionsSelected,
    learningItemsTotal,
    learningItemsSelected,
    notScheduled,
  };
};

const createSections = (details) => {
  let sections = [];
  if (details && details.contentItems) {
    sections = details.contentItems.map((section) => ({
      id: section.id,
      title: section.title,
      contentItems: section.contentItems?.map((item) => createContentItem(item)),
    }));
  }
  sections.forEach((section) => {
    const updatedSection = section;
    updatedSection.learningSelections = calculateSelectedItems(section);
  });
  return sections;
};

export const isLaunchFastTrackEnabled = (sections) => {
  if (sections === undefined || sections.length === 0) return false;
  const objectTypes = [];
  let hasExpertCoaching = false;
  let hasNonRemovedExpertCoaching = false;
  let totalCoachingSessions = 0;

  sections.forEach((section) => {
    // Track total coaching sessions from learningSelections
    if (section.learningSelections?.coachingSessionsTotal) {
      totalCoachingSessions += section.learningSelections.coachingSessionsTotal;
    }

    section.contentItems?.forEach((item) => {
      const itemType = item.type?.type;
      const isExpertCoaching = itemType === CONTENT_TYPES.LearnLiveCourse.type
        || itemType === CONTENT_TYPES.LearnLiveClass.type;

      if (isExpertCoaching) {
        hasExpertCoaching = true;
        if (!item.isRemoved) {
          hasNonRemovedExpertCoaching = true;
        }
      }

      if (!item.isRemoved) {
        objectTypes.push(itemType);
      }
    });
  });

  // Disable if there are any non-removed LearnLiveCourse items (existing condition)
  if (objectTypes.includes(CONTENT_TYPES.LearnLiveCourse.type)) {
    return false;
  }

  // Disable if there was expert coaching but all of it has been removed (new condition)
  if (hasExpertCoaching && !hasNonRemovedExpertCoaching) {
    return false;
  }

  // Disable if the template has no coaching sessions at all (new condition)
  if (totalCoachingSessions === 0) {
    return false;
  }

  return true;
};

export const formatFastTrackRequestDetailsToComponentData = (requestDetails, isUserCoachingManager, requestViewOnly, userId, picklists) => {
  const returnValue = {};
  if (typeof requestDetails === 'undefined') {
    return returnValue;
  }

  const editableOverride = userId === requestDetails?.coach?.userId || isUserCoachingManager;
  const fastTrackDetails = requestDetails.createdEntity;
  returnValue.status = makeFieldObject({
    id: requestDetails.status.id,
    value: requestDetails.status.label,
    type: 'chipSelect',
    placeHolder: 'Select',
    editable: true,
    coachingRequestId: requestDetails.coachingRequestId,
    updateColumnName: 'CoachingRequestStatusId',
    updateField: 'status',
    updateValueKey: 'id',
  });
  returnValue.customerInformation = {
    identity: [
      makeFieldObject({
        coachingRequestId: requestDetails?.coachingRequestId,
        updateColumnName: 'CustomerCompany',
        updateField: 'customerAccount.company',
        value:
          requestDetails?.customerAccount !== null
            ? requestDetails?.customerAccount.company
            : '',
        label: 'Company',
        displayLabel: '',
        editable: false,
        showTooltip: !(isUserCoachingManager && !requestViewOnly),
      }),
      makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        label: 'Subscription Id',
        value:
          requestDetails?.subscriptionId !== null
            ? requestDetails?.subscriptionId
            : '',
        displayLabel: '',
        editable: false,
        showTooltip: requestViewOnly,
        additionalInfo: [
          {
            coachingRequestId: requestDetails.coachingRequestId,
            value: 'BUSINESS SUCCESS PLAN',
            displayLabel: '',
            editable: false,
            showTooltip: false,
          },
        ],
      }),
      makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Account CSN',
        updateField: 'accountCSN',
        label: 'Account CSN',
        value:
          requestDetails?.accountCSN !== null ? requestDetails?.accountCSN : '',
        displayLabel: '',
        editable: !requestViewOnly,
        showTooltip: requestViewOnly,
      }),
    ],
    requestor: [
      makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value:
          requestDetails?.receivedDate !== null
            ? formatDate(requestDetails?.receivedDate)
            : '',
        label: 'Request Date',
        editable: false,
        type: 'datetime',
      }),
      makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: `${requestDetails?.customerAccount?.firstName
          ? `${requestDetails?.customerAccount?.firstName} `
          : ''
          }${requestDetails?.customerAccount?.lastName || ''}`,
        editable: false,
        label: 'Requestor',
        additionalInfo: [
          {
            coachingRequestId: requestDetails.coachingRequestId,
            value:
              requestDetails?.customerAccount !== null
                ? requestDetails?.customerAccount?.email
                : '',
            editable: false,
            email: true,
          },
        ],
      }),
      makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        value: `${requestDetails?.submitter?.firstName
          ? `${requestDetails?.submitter?.firstName} `
          : ''
          }${requestDetails?.submitter?.lastName || ''}`,
        editable: false,
        type: 'default',
        id:
          requestDetails.submitter !== null
            ? requestDetails.submitter.userId
            : '',
        label: 'Referred by',
        additionalInfo: [
          {
            coachingRequestId: requestDetails.coachingRequestId,
            value:
              requestDetails?.submitter !== null
                ? requestDetails?.submitter?.email
                : '',
            editable: false,
            type: 'default',
            id:
              requestDetails.submitter !== null
                ? requestDetails.submitter.userId
                : '',
            email: true,
          },
        ],
      }),
    ],
    trackTitle: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'TrackTitle',
        updateField: 'trackTitle',
        value: requestDetails?.requestedEntity?.title ? requestDetails.requestedEntity.title : '',
        editable: false,
        showTooltip: false,
        type: 'text',
      }),
    },
    objectives: {
      ...makeFieldObject({
        coachingRequestId: requestDetails?.coachingRequestId,
        updateColumnName: 'customerObjectives',
        updateField: 'customerObjectives',
        value: requestDetails?.customerObjectives !== null ? requestDetails.customerObjectives : '',
        editable: false,
        type: 'multiline',
      }),
    },
  };

  returnValue.location = {
    geo: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: undefined,
        updateField: 'geo',
        value: requestDetails?.geo !== null ? requestDetails?.geo?.label : '',
        label: 'Geo',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        isRequired: true,
        canBeBlank: true,
        placeHolder: 'Select Geo',
        selectOption: picklists.geo,
        updateObject: (value) => ({
          id: value.value,
          label: value.label,
          value: value.label,
        }),
      }),
    },
    country: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'GeoCountryRelationId',
        updateField: 'country',
        value: requestDetails?.country !== null ? requestDetails?.country.label : '',
        label: 'Country',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'select',
        disabled: true,
        selectOption: picklists.country,
        placeHolder: 'Select Country',
        updateValueKey: 'id',
        updateObject: (value) => ({
          id: value.value,
          label: value.label,
          value: value.label,
        }),
      }),
    },
    language: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'LanguageCode',
        updateField: 'language',
        value: requestDetails?.language ? getLanguageDisplayName(requestDetails.language) : '',
        label: 'Language',
        editable: false,
        showTooltip: false,
        type: 'text',
      }),
    },
  };
  returnValue.schedule = {
    discoveryDate: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Discovery Date',
        updateField: 'discoveryDate',
        placeHolder: 'MM/DD/YYYY',
        value: requestDetails.discoveryDate !== null ? requestDetails.discoveryDate : '',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'datetime',
      }),
    },
    customerAvailability: requestDetails.customerAvailability.length > 0 ? requestDetails.customerAvailability : [],
  };
  returnValue.notes = {
    notes: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'Notes',
        updateField: 'notes',
        value: requestDetails.notes.length > 0 ? he.decode(requestDetails.notes[0].value) : '',
        editable: editableOverride || !requestViewOnly,
        showTooltip: !(editableOverride || !requestViewOnly),
        type: 'multiline',
        updateObject: (value) => ([{
          userId: value.userId,
          value,
          dateCreated: Date.now(),
        }]),
        updateValueKey: 'value',
      }),
    },
    lastModifiedDate: requestDetails.lastModifiedDate !== null ? requestDetails.lastModifiedDate : '',
  };
  const { requestedEntity } = requestDetails;
  returnValue.details = {
    trackTitle: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'TrackTitle',
        updateField: 'trackTitle',
        value: fastTrackDetails?.title || '',
        editable: false,
        showTooltip: false,
        type: 'text',
      }),
    },
    trackDescription: {
      ...makeFieldObject({
        coachingRequestId: requestDetails.coachingRequestId,
        updateColumnName: 'TrackDescription',
        updateField: 'trackDescription',
        value: fastTrackDetails?.description || '',
        editable: false,
        showTooltip: false,
        type: 'multiline',
      }),
    },
    fastTrackFields: [
      {
        ...makeFieldObject({
          coachingRequestId: requestDetails.coachingRequestId,
          label: 'Product',
          value: (fastTrackDetails?.products && fastTrackDetails.products.length > 0)
            ? fastTrackDetails.products.map((product) => product.displayName).join(', ')
            : requestedEntity?.products?.map((product) => product.displayName).join(', '),
          editable: false,
          showTooltip: false,
          type: 'text',
        }),
      },
      {
        ...makeFieldObject({
          coachingRequestId: requestDetails.coachingRequestId,
          label: 'Industry',
          value: (fastTrackDetails?.industries && fastTrackDetails.industries.length > 0)
            ? fastTrackDetails.industries.map((industry) => industry.displayName).join(', ')
            : requestedEntity?.industries?.map((industry) => industry.displayName).join(', '),
          editable: false,
          showTooltip: false,
          type: 'text',
        }),
      },
      {
        ...makeFieldObject({
          coachingRequestId: requestDetails.coachingRequestId,
          label: 'Capabilities',
          value: (fastTrackDetails?.capabilities && fastTrackDetails.capabilities.length > 0)
            ? fastTrackDetails.capabilities.map((capability) => capability.label).join(', ')
            : requestedEntity?.capabilities?.map((capability) => capability.label).join(', '),
          editable: false,
          showTooltip: false,
          type: 'text',
        }),
      },
      {
        ...makeFieldObject({
          coachingRequestId: requestDetails.coachingRequestId,
          label: 'Outcomes',
          value: (fastTrackDetails?.outcomes && fastTrackDetails.outcomes.length > 0)
            ? fastTrackDetails.outcomes.map((outcome) => outcome.label).join(', ')
            : requestedEntity?.outcomes?.map((outcome) => outcome.label).join(', '),
          editable: false,
          showTooltip: false,
          type: 'text',
        }),
      },

    ],
    sections: createSections(fastTrackDetails),
    status: makeFieldObject({
      id: requestDetails.status?.id,
      value: requestDetails.status?.label,
      type: 'chipSelect',
      placeHolder: 'Select',
      editable: true,
      coachingRequestId: requestDetails?.coachingRequestId,
      updateColumnName: 'CoachingRequestStatusId',
      updateField: 'status',
      updateValueKey: 'id',
    }),
  };
  return returnValue;
};

export const initialState = {};
